import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Languages, ScanSearch, LogOut, Settings, Sparkles, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { LawVoLogo } from '../components/LawVoLogo';
import { useAuth } from '../context/AuthContext';
import { VERSION, CHANGELOG } from '../data/version';
export function Home() {
  const navigate = useNavigate();
  const {
    user,
    logout
  } = useAuth();
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const tools = [{
    id: 'translation',
    title: 'Translation Workspace',
    description: 'Translate legal documents between English and French with AI-powered drafts.',
    icon: Languages,
    path: '/translation',
    color: 'indigo'
  }, {
    id: 'gap-analysis',
    title: 'Legal Gap Analysis',
    description: 'Identify legal gaps, missing clauses, and risk exposure via multi-LLM review.',
    icon: ScanSearch,
    path: '/gap-analysis',
    color: 'purple'
  }];
  const latest = CHANGELOG[0];
  return <div className="min-h-screen bg-slate-50" data-id="element-456">
      {/* Header */}
      <header className="bg-white border-b border-slate-200" data-id="element-457">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between gap-4" data-id="element-458">
          <div className="flex items-center gap-3" data-id="element-459">
            <LawVoLogo size="md" data-id="element-460" />
            <button onClick={() => setIsChangelogOpen(true)} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 text-[11px] font-semibold tracking-wide transition-colors" title={`What's new in v${VERSION}`} data-id="element-461">
              
              <Sparkles className="w-3 h-3" data-id="element-462" />v{VERSION}
            </button>
          </div>
          <div className="flex items-center gap-4" data-id="element-463">
            {user?.email && <span className="text-sm text-slate-600" data-id="element-464">{user.email}</span>}
            <Link to="/settings" className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" title="Settings" data-id="element-465">
              
              <Settings className="w-5 h-5" data-id="element-466" />
            </Link>
            <button onClick={() => {
            logout();
            navigate('/login');
          }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-467">
              
              <LogOut className="w-4 h-4" data-id="element-468" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-8 py-16" data-id="element-469">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-470">
          
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-471">
            Legal Workspace
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-472">
            Professional tools for legal document translation and analysis.
            Choose a workspace to get started.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-473">
          {tools.map((tool, index) => <motion.div key={tool.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-474">
            
              <Link to={tool.path} className="group block bg-white rounded-xl border border-slate-200 p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-475">
              
                <div className={`inline-flex p-3 rounded-lg mb-4 ${tool.color === 'indigo' ? 'bg-indigo-50' : 'bg-purple-50'}`} data-id="element-476">
                
                  <tool.icon className={`w-8 h-8 ${tool.color === 'indigo' ? 'text-indigo-600' : 'text-purple-600'}`} data-id="element-477" />
                
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors" data-id="element-478">
                  {tool.title}
                </h2>
                <p className="text-slate-600 mb-4" data-id="element-479">{tool.description}</p>
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-600 group-hover:gap-3 transition-all" data-id="element-480">
                  Open Workspace
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-481">
                  
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-482" />
                  
                  </svg>
                </div>
              </Link>
            </motion.div>)}
        </div>

        {/* Latest-change teaser */}
        {latest && <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} onClick={() => setIsChangelogOpen(true)} className="mt-10 mx-auto flex items-start gap-3 max-w-2xl w-full text-left bg-white border border-slate-200 rounded-xl px-5 py-4 hover:border-indigo-200 hover:shadow-sm transition-all" data-id="element-483">
          
            <div className="shrink-0 p-1.5 bg-indigo-50 rounded-md text-indigo-600" data-id="element-484">
              <Sparkles className="w-4 h-4" data-id="element-485" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-486">
              <div className="flex items-center gap-2 mb-1" data-id="element-487">
                <span className="text-xs font-semibold uppercase tracking-wider text-indigo-600" data-id="element-488">
                  Latest update · v{latest.version}
                </span>
                <span className="text-xs text-slate-400" data-id="element-489">{latest.date}</span>
              </div>
              <p className="text-sm text-slate-700 line-clamp-2" data-id="element-490">
                {latest.changes[0]}
              </p>
            </div>
            <span className="shrink-0 text-xs font-medium text-indigo-600 self-center" data-id="element-491">
              View all →
            </span>
          </motion.button>}
      </main>

      {/* Changelog Modal */}
      <AnimatePresence data-id="element-492">
        {isChangelogOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsChangelogOpen(false)} className="fixed inset-0 bg-slate-900/40 z-40" data-id="element-493" />
          
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 240
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-494">
            
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col pointer-events-auto overflow-hidden" data-id="element-495">
                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100" data-id="element-496">
                  <div className="flex items-center gap-3" data-id="element-497">
                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600" data-id="element-498">
                      <Sparkles className="w-5 h-5" data-id="element-499" />
                    </div>
                    <div data-id="element-500">
                      <h2 className="text-lg font-semibold text-slate-900" data-id="element-501">
                        What's new
                      </h2>
                      <p className="text-xs text-slate-500" data-id="element-502">
                        Current version · v{VERSION}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setIsChangelogOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Close changelog" data-id="element-503">
                  
                    <X className="w-5 h-5" data-id="element-504" />
                  </button>
                </div>
                <div className="overflow-y-auto px-6 py-5 space-y-6" data-id="element-505">
                  {CHANGELOG.map(entry => <div key={entry.version} data-id="element-506">
                      <div className="flex items-baseline gap-2 mb-2" data-id="element-507">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold tracking-wide" data-id="element-508">
                          v{entry.version}
                        </span>
                        <span className="text-xs text-slate-400" data-id="element-509">
                          {entry.date}
                        </span>
                      </div>
                      <ul className="space-y-1.5 pl-1" data-id="element-510">
                        {entry.changes.map((change, idx) => <li key={idx} className="text-sm text-slate-700 leading-relaxed flex gap-2" data-id="element-511">
                      
                            <span className="text-indigo-400 shrink-0" data-id="element-512">•</span>
                            <span data-id="element-513">{change}</span>
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}